import React, { useEffect, useMemo, useRef, useState } from 'react'
import { makeStyles, Button, Grid, Hidden, IconButton, Link, Spinner, Typography } from '@paintscout/ui'
import { EstimatorSelect, KeypadInputField, DropdownSelect, useClientOptions, useUsers } from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import CloseIcon from '@material-ui/icons/Close'
import ZoomInIcon from '@material-ui/icons/ZoomIn'
import ZoomOutIcon from '@material-ui/icons/ZoomOut'
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos'
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos'
import { DndProvider, useDragLayer } from 'react-dnd'
import { TouchBackend } from 'react-dnd-touch-backend'
import { HTML5Backend } from 'react-dnd-html5-backend'
import FilterListIcon from '@material-ui/icons/FilterList'
import { getObjectLabels } from '@paintscout/util/builder/util/get-object-labels'
import { getQuoteTypeOptions } from '@paintscout/util/builder/options/get-quote-type-options'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { addAuth0Prefix } from '@paintscout/util/users/addAuth0Prefix'
import { removeAuth0Prefix } from '@paintscout/util/users/removeAuth0Prefix'
import type { CalendarProps } from 'react-big-calendar'
import type { CalendarEvent } from 'paintscout'
import { formatName } from '@paintscout/util/builder'
import { getCrews } from '@paintscout/util/builder/options'
import moment from 'moment'
import AgendaHeaderLeftColumn from './AgendaHeaderLeftColumn'
import CrewLeftColumnItem from './CrewLeftColumnItem'
import {
  getCrewIdsForProjectManager,
  getCrewsForProjectManager,
  getFilteredAndOrderedProjectManagerIds
} from './eventsAgendaDerivation'
import {
  countActiveFilterFields,
  getDesktopFilterButtonClassName,
  getMobileFilterButtonClassName
} from './filterButtonHelpers'
import { useProductionCalendar } from './ProductionCalendarContext'
import ProjectManagerRow from './ProjectManagerRow'
import TimelineHeader from './TimelineHeader'
import type { ZoomLevel } from './TimelineHeader'
import UnscheduledJobs from './UnscheduledJobs'
import { getProjectTypeLabel } from './util'

// Touch-capable devices (phones/tablets) use TouchBackend; desktop browsers
// (including Safari) use HTML5Backend which relies on the native DnD API.
// Safari desktop does not fire mousemove during a drag, so TouchBackend's
// enableMouseEvents fallback never gets position updates there.
// Note: Apple Silicon Macs report maxTouchPoints > 0 even though they are
// desktop machines, so we exclude macOS explicitly from the touch path.
const isMacOS = typeof window !== 'undefined' && /Macintosh|MacIntel|MacPPC|Mac68K/.test(navigator.userAgent)
const isTouchCapable =
  typeof window !== 'undefined' && !isMacOS && ('ontouchstart' in window || navigator.maxTouchPoints > 0)
const SWIPE_THRESHOLD = 60

const SPACE_BETWEEN = 'space-between'
const BORDER_SLATE = '1px solid rgba(226,232,240,0.6)'
const TRANSLATE_Y_CENTER = 'translateY(-50%)'
const FLEX_START = 'flex-start'
const DATE_FORMAT_FULL = 'MMM D, YYYY'

const useStyles = makeStyles<Theme, AgendaViewProps>(
  (theme) => ({
    root: {
      '& *': {
        userSelect: 'none',
        WebkitUserSelect: 'none'
      }
    },
    unscheduledContainer: {
      padding: theme.spacing(2)
    },
    unscheduledBody: {
      // add the equivalent of   shadow-lg  from tailwindcss
      boxShadow: theme.shadows[4]
    },
    unscheduledSummary: {
      flexDirection: 'row-reverse'
    },
    unscheduledSummaryContent: {
      justifyContent: SPACE_BETWEEN
    },
    unscheduledTitle: {
      marginLeft: theme.spacing(1)
    },
    unscheduledDetails: {
      display: 'flex',
      flexWrap: 'wrap',
      // wrap in even 4 per row with gaps
      gap: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingBottom: theme.spacing(2)
    },
    filterButton: {
      height: theme.spacing(4.5),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5, 3)
    },
    mobileFilterButton: {
      position: 'relative',
      paddingRight: 0,
      paddingLeft: 0
    },
    filterButtonSecondary: {
      color: theme.paintscout.palette.brand.Neutral.P30,
      backgroundColor: theme.paintscout.palette.brand.Neutral.P90,
      '&&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P90
      },
      '&&:active': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P90
      },
      '&&:focus': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P80
      }
    },
    filterButtonPrimary: {
      color: theme.palette.background.paper,
      backgroundColor: theme.palette.primary.main,
      '&&:hover': {
        backgroundColor: theme.palette.primary.dark
      },
      '&&:active': {
        backgroundColor: theme.palette.primary.main
      },
      '&&:focus': {
        backgroundColor: theme.palette.primary.main
      }
    },
    scheduleBody: {},
    timelineFilterHeader: {
      display: 'flex',
      justifyContent: SPACE_BETWEEN,
      alignItems: 'center',
      padding: theme.spacing(2),
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.paintscout.palette.brand.Neutral.P98
    },
    timelineFilterBody: {
      padding: theme.spacing(2),
      backgroundColor: theme.paintscout.palette.brand.Neutral.P98,
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    timelineFilterInputContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: theme.spacing(3),
      [theme.breakpoints.up('md')]: {
        gap: theme.spacing(4)
      }
    },
    timelineFilterTopDiv: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        gap: theme.spacing(1)
      }
    },
    timelineFilterInnerButtonContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: SPACE_BETWEEN,
      gap: theme.spacing(2),
      marginTop: theme.spacing(3),
      paddingTop: theme.spacing(3),
      borderTop: `1px solid #d9dee8`
    },
    noDataContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(6, 2),
      gap: theme.spacing(3),
      textAlign: 'center'
    },
    mobileTimelineFilterHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: SPACE_BETWEEN,
      gap: theme.spacing(2),
      padding: theme.spacing(1),
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.paintscout.palette.brand.Neutral.P98
    },
    timelineNavigationHeader: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      padding: theme.spacing(1, 2)
    },
    agendaContent: {
      display: 'flex',
      flexDirection: 'column'
    },
    stickyBarStyle: {
      position: 'sticky',
      top: 0,
      zIndex: 30,
      background: 'linear-gradient(to bottom, rgba(241,245,249,0.5), #fff)', // slate-50/50 to white
      borderBottom: BORDER_SLATE, // slate-200/60
      padding: '0.75rem', // px-3 py-3
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '0.125rem'
    },
    agendaFlexContainer: {
      display: 'flex',
      flex: 1,
      position: 'relative'
    },
    agendaFlexScroll: {
      flex: 1,
      overflowX: 'scroll',
      overflowY: 'visible'
    },
    swipeIndicator: {
      position: 'absolute',
      top: '50%',
      transform: TRANSLATE_Y_CENTER,
      zIndex: 100,
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 36,
      height: 36,
      borderRadius: '50%',
      background: 'rgba(0,0,0,0.18)',
      color: '#fff',
      transition: 'background 0.15s ease, transform 0.15s ease'
    },
    swipeIndicatorLeft: {
      left: 12
    },
    swipeIndicatorRight: {
      right: 12
    },
    agendaMinWidth: {
      display: 'flex',
      minWidth: 'max-content'
    },
    agendaLeftColumn: {
      flexShrink: 0,
      borderRight: BORDER_SLATE, // border-slate-200/60
      background: '#fff',
      position: 'sticky',
      left: 0,
      zIndex: 80,
      boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)',
      transition: 'width 0.3s ease' // <-- Add this line for smooth transition
    },
    agendaDayHeaderSpacer: {
      position: 'sticky',
      top: 0,
      zIndex: 20,
      borderBottom: BORDER_SLATE,
      background: '#fff',
      height: 80
    },
    agendaLeadRows: {
      borderBottom: '1px solid #e5e7eb', // divide-y divide-gray-200
      '& > *:not(:last-child)': {
        borderBottom: '1px solid #e5e7eb'
      }
    },
    agendaManagerRow: {
      display: 'flex',
      alignItems: 'center',
      // padding: '8px 8px',
      overflow: 'hidden',
      backgroundColor: 'rgba(241, 245, 249, 0.5)' // bg-slate-100/50
      // borderTop: '1px solid rgba(203, 213, 225, 0.4)', // border-y (top)
      // borderBottom: '1px solid rgba(203, 213, 225, 0.4)' // border-y (bottom)
    },
    agendaLeadManagerTooltipContainer: {
      position: 'absolute',
      zIndex: 4000
    },
    agendaLeadRowDay: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 0'
    },
    agendaLeftColumnInner: {
      flex: 1,
      overflow: 'visible'
    },
    agendaTimelineHeader: {
      position: 'sticky',
      top: 0,
      zIndex: 70,
      background: '#fff'
    },
    hoverShadowBar: {
      position: 'absolute',
      bottom: -24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
      zIndex: 25,
      boxShadow: '0 4px 8px -2px rgba(0, 0, 0, 0.15)',
      borderBottomLeftRadius: 4,
      borderBottomRightRadius: 4,
      transition: 'left 0.12s steps(1, end), width 0.12s steps(1, end), opacity 0.15s ease'
    },
    hoverShadowText: {
      fontSize: '0.7rem',
      fontWeight: 600,
      color: '#fff',
      whiteSpace: 'nowrap',
      textShadow: '0 1px 2px rgba(0,0,0,0.2)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      padding: '0 6px'
    },
    // agendaResizeHandle: {
    //   position: 'absolute',
    //   top: '50px',
    //   width: '4px',
    //   // height: '15px',
    //   cursor: 'col-resize',
    //   background: 'transparent',
    //   transition: 'background 0.2s, width 0.2s',
    //   zIndex: 40,
    //   pointerEvents: 'auto',
    //   '&:hover': {
    //     background: '#60a5fa', // blue-400
    //     width: '8px'
    //   }
    // },
    agendaResizeHandleButton: {
      position: 'absolute',
      top: '40%',
      left: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: FLEX_START,
      width: '20px',
      height: '40px',
      borderRadius: `0 ${theme.borderRadius.md} ${theme.borderRadius.md} 0`,
      border: `1px solid ${theme.palette.divider}`,
      background: theme.paintscout.palette.brand.Neutral.P98,
      cursor: 'pointer',
      zIndex: 80,
      pointerEvents: 'auto',
      transition: 'background 0.3s ease',
      '&:hover': {
        background: theme.paintscout.palette.brand.Neutral.P90,
        boxShadow: `0 0 0 2px ${theme.paintscout.palette.brand.Neutral.P80}` // blue-400 at 50% opacity
      }
    },
    navigationSpan: {
      display: 'flex',
      borderBottom: '1px solid rgba(226, 232, 240, 0.6)', // border-slate-200/60
      padding: theme.spacing(2),
      paddingRight: theme.spacing(1),
      alignItems: 'center',
      justifyContent: SPACE_BETWEEN,
      gap: theme.spacing(0.5)
    },
    dateLabel: {
      [theme.breakpoints.down('xs')]: {
        fontSize: '0.75rem'
      }
    },
    navigationButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '32px',
      padding: theme.spacing(0.5)
    },
    unscheduledFilterButtonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    unscheduledCloseIcon: {
      width: theme.spacing(2),
      height: theme.spacing(2)
    },
    mobileFilterCount: {
      position: 'absolute',
      top: -5,
      right: -5,
      border: `1px solid ${theme.palette.background.paper}`,
      backgroundColor: theme.palette.primary.main,
      borderRadius: '9999px',
      height: theme.spacing(2.5),
      width: theme.spacing(2.5),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
      color: theme.palette.background.paper
    },
    filterCount: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.primary.main,
      borderRadius: '9999px',
      width: theme.spacing(2.5),
      height: theme.spacing(2.5),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.75rem',
      marginTop: theme.spacing(0.25),
      marginLeft: theme.spacing(1)
    },
    nameTooltip: {
      position: 'absolute',
      left: '100%',
      marginLeft: '8px',
      top: '0',
      transform: 'translate(70px, -150%)',
      backgroundColor: '#1e293b',
      color: '#fff',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '0.75rem',
      whiteSpace: 'nowrap',
      zIndex: 1500,
      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)'
    },
    tooltipArrow: {
      position: 'absolute',
      right: '100%',
      top: '50%',
      transform: TRANSLATE_Y_CENTER,
      borderWidth: '4px',
      borderStyle: 'solid',
      borderColor: 'transparent',
      borderRightColor: '#1e293b'
    }
  }),
  { name: 'EventsAgendaView' }
)

export interface AgendaViewProps extends CalendarProps {
  classes?: StyleClasses<typeof useStyles>
}

interface DragHoverShadowProps {
  startDate: Date
  endDate: Date
  cellWidth: number
  crews: Array<{ _id: string; color: string }>
  classes: Record<string, string>
}

interface HoverEventShadowProps {
  hoveredEventShadow: { left: number; width: number; crewColor: string; dateLabel: string } | null
  classes: Record<string, string>
}

function HoverEventShadow({ hoveredEventShadow, classes }: HoverEventShadowProps) {
  const { isDragging } = useDragLayer((monitor) => ({ isDragging: monitor.isDragging() }))
  if (isDragging || !hoveredEventShadow) return null
  return (
    <div
      className={classes.hoverShadowBar}
      style={{
        left: `${hoveredEventShadow.left}px`,
        width: `${hoveredEventShadow.width}px`,
        backgroundColor: hoveredEventShadow.crewColor + 'CC'
      }}
    >
      <span className={classes.hoverShadowText}>{hoveredEventShadow.dateLabel}</span>
    </div>
  )
}

const SCROLL_ZONE = 120 // px from edge that triggers scrolling
const MAX_SCROLL_SPEED = 18 // px per frame at the very edge

interface GlobalCalendarDragLayerProps {
  zoomLevel: ZoomLevel
  cellWidth: number
}

function GlobalCalendarDragLayer({ zoomLevel, cellWidth }: GlobalCalendarDragLayerProps) {
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    currentOffset: monitor.getClientOffset()
  }))
  const { options } = useClientOptions()

  if (!isDragging || !currentOffset || !item?.event || item.isUnscheduled) return null

  const crews = getCrews({ options })
  const crewColor = crews.find((c) => c._id === item.event.crew)?.color ?? '#94a3b8'

  const timeUnitInDays = zoomLevel === 'weeks' ? 7 : zoomLevel === 'months' ? 30 : zoomLevel === 'quarters' ? 90 : 1
  let widthPixels = 200
  if (item.event.start && item.event.end) {
    const s = new Date(item.event.start)
    s.setHours(0, 0, 0, 0)
    const e = new Date(item.event.end)
    e.setHours(0, 0, 0, 0)
    const durationDays = Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1
    widthPixels = Math.max(12, (durationDays / timeUnitInDays) * cellWidth - 8)
  }

  let snappedX = currentOffset.x
  const elements = document.elementsFromPoint(currentOffset.x, currentOffset.y)
  for (const el of elements) {
    const dayCell = el.closest('[data-date]')
    if (dayCell) {
      const prevCell = dayCell.previousElementSibling
      const targetCell = prevCell?.hasAttribute('data-date') ? prevCell : dayCell
      snappedX = (targetCell as HTMLElement).getBoundingClientRect().left
      break
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 9999,
        left: snappedX,
        top: currentOffset.y,
        transform: TRANSLATE_Y_CENTER
      }}
    >
      <div
        style={{
          height: '40px',
          borderRadius: 4,
          padding: '0 16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: FLEX_START,
          justifyContent: FLEX_START,
          fontSize: '0.75rem',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
          border: `1px solid ${crewColor}`,
          backgroundColor: `${crewColor}40`,
          width: widthPixels
        }}
      >
        <Typography variant={'h6'} color={'textPrimary'} weight={'bold'}>
          {item?.event?.deal?.name}
        </Typography>
        <Typography variant={'subtitle1'} color={'textSecondary'}>
          {getProjectTypeLabel(item?.event?.projectType, options)}
        </Typography>
      </div>
    </div>
  )
}

function DragAutoScroll() {
  const { isDragging, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    currentOffset: monitor.getClientOffset()
  }))

  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }

    if (!isDragging || !currentOffset) return

    const { y } = currentOffset
    const viewportHeight = window.innerHeight

    const distanceFromBottom = viewportHeight - y
    const distanceFromTop = y

    let speed = 0
    if (distanceFromBottom < SCROLL_ZONE) {
      speed = Math.round(MAX_SCROLL_SPEED * (1 - distanceFromBottom / SCROLL_ZONE))
    } else if (distanceFromTop < SCROLL_ZONE) {
      speed = -Math.round(MAX_SCROLL_SPEED * (1 - distanceFromTop / SCROLL_ZONE))
    }

    if (speed === 0) return

    const scroll = () => {
      window.scrollBy({ top: speed, behavior: 'instant' as ScrollBehavior })
      rafRef.current = requestAnimationFrame(scroll)
    }
    rafRef.current = requestAnimationFrame(scroll)

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [isDragging, currentOffset?.y])

  return null
}

function DragHoverShadow({ startDate, endDate, cellWidth, crews, classes }: DragHoverShadowProps) {
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    currentOffset: monitor.getClientOffset()
  }))

  if (!isDragging || !currentOffset || !item?.event) return null

  // Find the date cell under the cursor, looking through all layers
  // (elementFromPoint only returns the topmost element, which may be a ProjectBlock).
  // Shift one cell left via previousElementSibling to match the drag preview snap.
  const elements = document.elementsFromPoint(currentOffset.x, currentOffset.y)
  let dateStr: string | null = null
  for (const el of elements) {
    const dayCell = el.closest('[data-date]')
    if (dayCell) {
      const prevCell = dayCell.previousElementSibling
      const targetCell = prevCell?.hasAttribute('data-date') ? prevCell : dayCell
      dateStr = targetCell.getAttribute('data-date')
      break
    }
  }
  if (!dateStr) return null

  const event = item.event
  const duration =
    event.start && event.end
      ? Math.round((new Date(event.end).getTime() - new Date(event.start).getTime()) / (1000 * 60 * 60 * 24)) + 1
      : item.duration || 7

  const [year, month, day] = dateStr.split('-').map(Number)
  const hoverDate = new Date(year, month - 1, day)
  hoverDate.setHours(0, 0, 0, 0)

  const normalizedStart = new Date(startDate)
  normalizedStart.setHours(0, 0, 0, 0)

  const daysFromStart = Math.round((hoverDate.getTime() - normalizedStart.getTime()) / (1000 * 60 * 60 * 24))

  const rawLeft = daysFromStart * cellWidth
  const left = Math.max(0, rawLeft)
  const clipped = left - rawLeft
  const normalizedEnd = new Date(endDate)
  normalizedEnd.setHours(0, 0, 0, 0)
  const totalDays = Math.round((normalizedEnd.getTime() - normalizedStart.getTime()) / (1000 * 60 * 60 * 24)) + 1
  const width = Math.min(duration * cellWidth - clipped, totalDays * cellWidth - left)

  if (width <= 0) return null

  const shadowEnd = new Date(hoverDate)
  shadowEnd.setDate(shadowEnd.getDate() + duration - 1)

  const formatDateStr = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

  const dateLabel =
    duration === 1 ? formatDateStr(hoverDate) : `${formatDateStr(hoverDate)} - ${formatDateStr(shadowEnd)}`

  const crewColor = crews.find((c) => c._id === event.crew)?.color || '#94a3b8'

  return (
    <div
      className={classes.hoverShadowBar}
      style={{
        left: `${left}px`,
        width: `${width}px`,
        backgroundColor: crewColor + 'CC'
      }}
    >
      <span className={classes.hoverShadowText}>{dateLabel}</span>
    </div>
  )
}

function EventsAgendaView(props: AgendaViewProps) {
  const classes = useStyles(props)
  const {
    targetDate,
    setVisibleWindow,
    startDate,
    endDate,
    visibleEvents: events,
    updateEvent,
    loading,
    unscheduledEvents,
    scheduledFilters,
    zoomLevel,
    setZoomLevel,
    monthColumns,
    setMonthColumns,
    quarterColumns,
    setQuarterColumns
  } = useProductionCalendar()
  const { options } = useClientOptions()
  const { canDeactivateSettingsItems } = useFlags()
  const crews = getCrews({ options })
  const { users } = useUsers()
  const { quote: quoteLabel, currency } = getObjectLabels({ options })
  const typeOptions = getQuoteTypeOptions({ options, hideAll: false, canDeactivate: !!canDeactivateSettingsItems }).map(
    (type) => ({
      value: type.key,
      label: type.label
    })
  )
  const [showNameTooltip, setShowNameTooltip] = useState(null)
  const [timelineFilterOpen, setTimelineFilterOpen] = useState(false)
  const [hoveredProject, setHoveredProject] = useState<string | null>(null)
  const [hoveredDate, setHoveredDate] = useState<string | null>(null)

  // Compute shadow bar position for hovered project
  const cellWidth = 80 // Must match timelineCellWidth
  const hoveredEventShadow = useMemo(() => {
    if (!hoveredProject || zoomLevel !== 'days') return null
    const hoveredEvent = events.find((e) => e.id === hoveredProject)
    if (!hoveredEvent || !hoveredEvent.start || !hoveredEvent.end) return null

    const eventStart = new Date(hoveredEvent.start)
    eventStart.setHours(0, 0, 0, 0)
    const eventEnd = new Date(hoveredEvent.end)
    eventEnd.setHours(0, 0, 0, 0)

    const normalizedStart = new Date(startDate)
    normalizedStart.setHours(0, 0, 0, 0)

    const daysFromStart = Math.round((eventStart.getTime() - normalizedStart.getTime()) / (1000 * 60 * 60 * 24))
    const projectDuration = Math.round((eventEnd.getTime() - eventStart.getTime()) / (1000 * 60 * 60 * 24)) + 1

    const normalizedEnd = new Date(endDate)
    normalizedEnd.setHours(0, 0, 0, 0)
    const totalDays = Math.round((normalizedEnd.getTime() - normalizedStart.getTime()) / (1000 * 60 * 60 * 24)) + 1

    const rawLeft = daysFromStart * cellWidth
    const left = Math.max(0, rawLeft)
    const clipped = left - rawLeft
    const width = Math.min(projectDuration * cellWidth - clipped, totalDays * cellWidth - left)

    if (width <= 0) return null

    const crewId = hoveredEvent.crew
    const crew = crews.find((c) => c._id === crewId)
    const crewColor = crew?.color || '#94a3b8'

    const formatDate = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

    const dateLabel =
      projectDuration === 1 ? formatDate(eventStart) : `${formatDate(eventStart)} - ${formatDate(eventEnd)}`

    return { left, width, crewColor, dateLabel }
  }, [hoveredProject, events, startDate, endDate, zoomLevel, crews, cellWidth])

  // Single source of truth for the target date - now as state
  // Initialize with today minus 2 days so today appears as the 3rd visible day
  // const [targetDate, setTargetDate] = useState(() => {
  // const today = new Date(Date.now())
  // today.setDate(today.getDate() - 2) // Start 2 days before today
  // return today
  // })

  // Lead and crew ordering
  const [projectManagerOrder, setProjectManagerOrder] = useState<string[]>([])
  const [crewOrderByLead, _setCrewOrderByLead] = useState<Record<string, string[]>>({})

  // Resizable left column width - responsive to screen size
  // Mobile: 72px (just avatar + minimal padding), Desktop: 200px (full info)
  const [leftColumnWidth, setLeftColumnWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768 ? 72 : 200
    }
    return 200
  })
  const timelineContainerRef = useRef<HTMLDivElement>(null)
  const [timelineContainerWidth, setTimelineContainerWidth] = useState(0)
  const [isResizing, setIsResizing] = useState(false)
  const resizeStartX = useRef(0)
  const resizeStartWidth = useRef(256)

  // Swipe-to-navigate refs (touch only, no re-renders needed)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const swipeTouchStartX = useRef(0)
  const swipeEdgeTouchX = useRef<number | null>(null)
  const swipeHitEdge = useRef<'left' | 'right' | null>(null)
  const swipeOverscroll = useRef(0)
  const swipeIndicatorLeftRef = useRef<HTMLDivElement>(null)
  const swipeIndicatorRightRef = useRef<HTMLDivElement>(null)
  // const handleResizeStart = (e: React.MouseEvent<HTMLDivElement>) => {
  //   e.preventDefault()
  //   setIsResizing(true)
  //   resizeStartX.current = e.clientX
  //   resizeStartWidth.current = leftColumnWidth
  // }

  // Local Filter states for timeline
  const [selectedProjectTypes, setSelectedProjectTypes] = useState<string[]>(
    scheduledFilters.scheduledSelectedProjectTypes
  )
  const [selectedProjectManagers, setSelectedProjectManagers] = useState<string[]>(
    scheduledFilters.scheduledSelectedProjectManagers
  )
  const [selectedTimelineSalesReps, setSelectedTimelineSalesReps] = useState<string[]>(
    scheduledFilters.scheduledSelectedSalesReps
  )
  const [minTimelinePrice, setMinTimelinePrice] = useState(scheduledFilters.scheduledMinPrice)
  const [maxTimelinePrice, setMaxTimelinePrice] = useState(scheduledFilters.scheduledMaxPrice)

  // Apply filters to scheduled events
  const filteredScheduledEvents = events

  const hasTimelineActiveFilters =
    scheduledFilters.scheduledSelectedProjectManagers.length > 0 ||
    scheduledFilters.scheduledSelectedSalesReps.length > 0 ||
    scheduledFilters.scheduledSelectedProjectTypes.length > 0 ||
    scheduledFilters.scheduledMinPrice !== null ||
    scheduledFilters.scheduledMaxPrice !== null
  const timelineActiveFilterCount = countActiveFilterFields([
    scheduledFilters.scheduledMinPrice !== null,
    scheduledFilters.scheduledMaxPrice !== null,
    scheduledFilters.scheduledSelectedProjectTypes.length > 0,
    scheduledFilters.scheduledSelectedProjectManagers.length > 0,
    scheduledFilters.scheduledSelectedSalesReps.length > 0
  ])
  const timelineDesktopFilterButtonClassName = getDesktopFilterButtonClassName({
    classes,
    isOpen: timelineFilterOpen
  })
  const timelineMobileFilterButtonClassName = getMobileFilterButtonClassName({
    classes,
    isOpen: timelineFilterOpen
  })

  const clearTimelineFilters = () => {
    scheduledFilters.setScheduledSelectedProjectTypes([])
    scheduledFilters.setScheduledSelectedProjectManagers([])
    scheduledFilters.setScheduledSelectedSalesReps([])
    scheduledFilters.setScheduledMinPrice('')
    scheduledFilters.setScheduledMaxPrice('')
    setSelectedTimelineSalesReps([])
    setSelectedProjectManagers([])
    setSelectedProjectTypes([])
    setMinTimelinePrice(null)
    setMaxTimelinePrice(null)
  }

  function handleSearch() {
    setTimelineFilterOpen(false)
    // Apply local filter values to the global unscheduledFilters
    // only on the values that have changed to avoid unnecessary re-filtering
    if (maxTimelinePrice !== scheduledFilters.scheduledMaxPrice) {
      scheduledFilters.setScheduledMaxPrice(maxTimelinePrice !== null ? String(maxTimelinePrice) : '')
    }
    if (minTimelinePrice !== scheduledFilters.scheduledMinPrice) {
      scheduledFilters.setScheduledMinPrice(minTimelinePrice !== null ? String(minTimelinePrice) : '')
    }
    if (selectedProjectManagers !== scheduledFilters.scheduledSelectedProjectManagers) {
      scheduledFilters.setScheduledSelectedProjectManagers(selectedProjectManagers)
    }
    if (selectedTimelineSalesReps !== scheduledFilters.scheduledSelectedSalesReps) {
      scheduledFilters.setScheduledSelectedSalesReps(selectedTimelineSalesReps)
    }
    if (selectedProjectTypes !== scheduledFilters.scheduledSelectedProjectTypes) {
      scheduledFilters.setScheduledSelectedProjectTypes(selectedProjectTypes)
    }
  }

  useEffect(() => {
    setSelectedProjectTypes(scheduledFilters.scheduledSelectedProjectTypes || [])
    setSelectedProjectManagers(scheduledFilters.scheduledSelectedProjectManagers || [])
    setSelectedTimelineSalesReps(scheduledFilters.scheduledSelectedSalesReps || [])
    setMinTimelinePrice(scheduledFilters.scheduledMinPrice)
    setMaxTimelinePrice(scheduledFilters.scheduledMaxPrice)
  }, [
    scheduledFilters.scheduledSelectedProjectTypes,
    scheduledFilters.scheduledSelectedProjectManagers,
    scheduledFilters.scheduledSelectedSalesReps,
    scheduledFilters.scheduledMinPrice,
    scheduledFilters.scheduledMaxPrice
  ])

  // Collapsed lead state
  const [collapsedLeads, setCollapsedLeads] = useState<Record<string, boolean>>({})

  const orderedProjectManagerIds = getFilteredAndOrderedProjectManagerIds({
    crews,
    selectedProjectManagers,
    projectManagerOrder
  })
  const visibleProjectManagerIds = useMemo(
    () =>
      orderedProjectManagerIds.filter((projectManagerId) => {
        if (!projectManagerId) return false
        const managersCrews = getCrewsForProjectManager(crews, projectManagerId)
        if (!managersCrews.length) return false
        return users.some((user) => user.user_id === projectManagerId)
      }),
    [orderedProjectManagerIds, crews, users]
  )
  const showProjectManagerControls = visibleProjectManagerIds.length > 1

  const zoomLevels: ZoomLevel[] = ['days', 'weeks', 'months', 'quarters']
  const currentZoomIndex = zoomLevels.indexOf(zoomLevel)

  const canZoomIn = currentZoomIndex > 0
  const canZoomOut = currentZoomIndex < zoomLevels.length - 1

  const getDateLabel = () => {
    const start = moment(startDate)
    const end = moment(endDate)
    const sameYear = start.year() === end.year()

    if (zoomLevel === 'quarters') {
      if (sameYear && start.quarter() === end.quarter()) {
        return `Q${start.quarter()} ${start.year()}`
      }
      return `Q${start.quarter()} ${start.year()} - Q${end.quarter()} ${end.year()}`
    }

    if (zoomLevel === 'months') {
      return sameYear
        ? `${start.format('MMM')} - ${end.format('MMM YYYY')}`
        : `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`
    }

    // weeks + days (currently identical — confirm if weeks should differ)
    return sameYear
      ? `${start.format('MMM D')} - ${end.format(DATE_FORMAT_FULL)}`
      : `${start.format(DATE_FORMAT_FULL)} - ${end.format(DATE_FORMAT_FULL)}`
  }

  const shiftDateByMonths = (date: Date, months: number) => {
    const shiftedDate = new Date(date)
    const originalDay = shiftedDate.getDate()
    shiftedDate.setDate(1)
    shiftedDate.setMonth(shiftedDate.getMonth() + months)
    const maxDayInTargetMonth = new Date(shiftedDate.getFullYear(), shiftedDate.getMonth() + 1, 0).getDate()
    shiftedDate.setDate(Math.min(originalDay, maxDayInTargetMonth))
    return shiftedDate
  }

  const handleZoomIn = () => {
    if (canZoomIn) {
      setZoomLevel(zoomLevels[currentZoomIndex - 1])
    }
  }

  const handleZoomOut = () => {
    if (canZoomOut) {
      setZoomLevel(zoomLevels[currentZoomIndex + 1])
    }
  }

  const handleNavigatePrevious = () => {
    const newDate = new Date(targetDate)
    switch (zoomLevel) {
      case 'days':
        newDate.setDate(newDate.getDate() - 7)
        break
      case 'weeks':
        newDate.setDate(newDate.getDate() - 4 * 7) // 4 weeks
        break
      case 'months':
        return setVisibleWindow(shiftDateByMonths(targetDate, -3))
      case 'quarters':
        return setVisibleWindow(shiftDateByMonths(targetDate, -12))
    }
    setVisibleWindow(newDate)
  }

  const handleNavigateNext = () => {
    const newDate = new Date(targetDate)
    switch (zoomLevel) {
      case 'days':
        newDate.setDate(newDate.getDate() + 7)
        break
      case 'weeks':
        newDate.setDate(newDate.getDate() + 4 * 7) // 4 weeks
        break
      case 'months':
        return setVisibleWindow(shiftDateByMonths(targetDate, 3))
      case 'quarters':
        return setVisibleWindow(shiftDateByMonths(targetDate, 12))
    }
    setVisibleWindow(newDate)
  }

  const resetSwipeState = () => {
    swipeEdgeTouchX.current = null
    swipeHitEdge.current = null
    swipeOverscroll.current = 0
    const leftEl = swipeIndicatorLeftRef.current
    const rightEl = swipeIndicatorRightRef.current
    if (leftEl) leftEl.style.display = 'none'
    if (rightEl) rightEl.style.display = 'none'
  }

  const handleScrollTouchStart = (e: React.TouchEvent) => {
    swipeTouchStartX.current = e.touches[0].clientX
    resetSwipeState()
  }

  const handleScrollTouchMove = (e: React.TouchEvent) => {
    const el = scrollContainerRef.current
    if (!el) return

    const touchX = e.touches[0].clientX
    const deltaX = touchX - swipeTouchStartX.current
    // deltaX < 0 means finger moved left → scrolling right → potentially next
    // deltaX > 0 means finger moved right → scrolling left → potentially prev

    const atRightEdge = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4
    const atLeftEdge = el.scrollLeft <= 4

    if (swipeEdgeTouchX.current === null) {
      if (atRightEdge && deltaX < -12) {
        swipeEdgeTouchX.current = touchX
        swipeHitEdge.current = 'right'
      } else if (atLeftEdge && deltaX > 12) {
        swipeEdgeTouchX.current = touchX
        swipeHitEdge.current = 'left'
      }
    }

    if (swipeEdgeTouchX.current !== null) {
      swipeOverscroll.current = Math.abs(touchX - swipeEdgeTouchX.current)
      const ready = swipeOverscroll.current >= SWIPE_THRESHOLD
      const indicatorEl =
        swipeHitEdge.current === 'left' ? swipeIndicatorLeftRef.current : swipeIndicatorRightRef.current
      if (indicatorEl) {
        indicatorEl.style.display = 'flex'
        indicatorEl.style.background = ready ? 'rgba(59,130,246,0.85)' : 'rgba(0,0,0,0.18)'
        indicatorEl.style.transform = ready ? `${TRANSLATE_Y_CENTER} scale(1.2)` : TRANSLATE_Y_CENTER
      }
    }
  }

  const handleScrollTouchEnd = () => {
    if (swipeOverscroll.current >= SWIPE_THRESHOLD) {
      if (swipeHitEdge.current === 'right') {
        handleNavigateNext()
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft = 0
        }
      } else if (swipeHitEdge.current === 'left') {
        handleNavigatePrevious()
        const el = scrollContainerRef.current
        if (el) {
          requestAnimationFrame(() => {
            el.scrollLeft = el.scrollWidth
            requestAnimationFrame(() => {
              el.scrollLeft = el.scrollWidth
            })
          })
        }
      }
    }
    resetSwipeState()
  }

  const handleGoToToday = () => {
    const todayDate = new Date(Date.now())
    todayDate.setDate(todayDate.getDate() - 2) // Start 2 days before today so today appears as 3rd column
    setVisibleWindow(todayDate)
  }

  const handleDrop = (eventId: string, crewId: string, newStartDate: Date) => {
    const event = [...events, ...unscheduledEvents].find((event) => event.id === eventId)

    if (!event) return

    // On touch devices (iPad/mobile), iOS Safari can shift the viewport after a
    // touch-drag ends when content reflows. Capture the scroll position now and
    // restore it after React re-renders settle (two rAF = after next paint).
    const savedScrollY = isTouchCapable ? window.scrollY : null

    // Calculate duration
    const currentDuration =
      event.start && event.end
        ? Math.ceil((new Date(event.end).getTime() - new Date(event.start).getTime()) / (1000 * 60 * 60 * 24))
        : 7 // Default 7 days for unscheduled projects

    const newEndDate = new Date(newStartDate)
    newEndDate.setDate(newEndDate.getDate() + currentDuration)

    const crew = crews.find((c) => c._id === crewId)
    const user = users.find((u) => removeAuth0Prefix(u.user_id) === removeAuth0Prefix(crew?.projectManager?.id))

    const updatedEvent: CalendarEvent = {
      ...event,
      crew: crewId,
      start: newStartDate,
      end: newEndDate,
      estimator:
        event.estimator ||
        (user
          ? {
              id: removeAuth0Prefix(user.user_id),
              name: formatName(user.user_metadata),
              email: user.email,
              color: user.user_metadata?.color
            }
          : undefined)
    }
    updateEvent(updatedEvent)

    if (savedScrollY !== null) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: savedScrollY, behavior: 'instant' as ScrollBehavior })
        requestAnimationFrame(() => {
          window.scrollTo({ top: savedScrollY, behavior: 'instant' as ScrollBehavior })
        })
      })
    }
  }

  const calculateCrewHeight = (crewEvents: CalendarEvent[]) => {
    // Sort events by start date to match CrewSubRow's row assignment behavior
    const sortedEvents = [...crewEvents].sort((a, b) => {
      if (!a.start) return 1
      if (!b.start) return -1
      return new Date(a.start).getTime() - new Date(b.start).getTime()
    })

    const rowOccupancy: Array<Array<{ start: Date; end: Date }>> = []

    sortedEvents.forEach((event) => {
      if (!event.start || !event.end) return

      // Normalize hours to match CrewSubRow's overlap detection
      const eventStart = new Date(event.start)
      eventStart.setHours(0, 0, 0, 0)
      const eventEnd = new Date(event.end)
      eventEnd.setHours(23, 59, 59, 999)

      let assignedRow = -1
      for (let rowIndex = 0; rowIndex < rowOccupancy.length; rowIndex++) {
        const eventsInRow = rowOccupancy[rowIndex]
        let hasOverlap = false

        for (const occupiedEvent of eventsInRow) {
          const { start, end } = occupiedEvent
          if (eventStart <= end && eventEnd >= start) {
            hasOverlap = true
            break
          }
        }

        if (!hasOverlap) {
          assignedRow = rowIndex
          break
        }
      }

      if (assignedRow === -1) {
        assignedRow = rowOccupancy.length
      }

      if (!rowOccupancy[assignedRow]) {
        rowOccupancy[assignedRow] = []
      }

      rowOccupancy[assignedRow].push({ start: eventStart, end: eventEnd })
    })

    const maxRows = rowOccupancy.length || 1
    // IMPORTANT: This must match the height calculation in CrewSubRow.tsx exactly!
    // 12px top padding + (rows * 60px) + 0px bottom padding
    // Job blocks are 48px, so with 60px row spacing, we get 12px above each job naturally
    return Math.max(60, 12 + maxRows * 60)
  }

  const availableTimelineWidth = Math.max(0, timelineContainerWidth - leftColumnWidth)
  const timelineCellWidth = 80
  const minMonthColumns = Math.max(1, Math.ceil(availableTimelineWidth / timelineCellWidth))
  const minQuarterColumns = Math.max(1, Math.ceil(availableTimelineWidth / timelineCellWidth))

  useEffect(() => {
    if (zoomLevel === 'months' && monthColumns !== minMonthColumns) {
      setMonthColumns(minMonthColumns)
    }
    if (zoomLevel === 'quarters' && quarterColumns !== minQuarterColumns) {
      setQuarterColumns(minQuarterColumns)
    }
  }, [zoomLevel, monthColumns, minMonthColumns, setMonthColumns, quarterColumns, minQuarterColumns, setQuarterColumns])

  const handleMoveManager = (draggedLeadId: string, targetLeadId: string) => {
    const currentOrder = projectManagerOrder.length > 0 ? projectManagerOrder : orderedProjectManagerIds
    const draggedIndex = currentOrder.indexOf(draggedLeadId)
    const targetIndex = currentOrder.indexOf(targetLeadId)
    if (draggedIndex === -1 || targetIndex === -1) return

    const newOrder = [...currentOrder]
    newOrder.splice(draggedIndex, 1)
    newOrder.splice(targetIndex, 0, draggedLeadId)
    setProjectManagerOrder(newOrder)
  }

  // Listen for window resize to update visible days
  useEffect(() => {
    const handleResize = () => {
      // Update left column width based on screen size if it's at the default values
      const isMobileNow = window.innerWidth < 768
      if (isMobileNow && leftColumnWidth > 150) {
        // Switching to mobile - use mobile default
        setLeftColumnWidth(72)
      } else if (!isMobileNow && leftColumnWidth < 150) {
        // Switching to desktop - use desktop default
        setLeftColumnWidth(200)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [leftColumnWidth])

  useEffect(() => {
    if (!isResizing) return
    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizeStartX.current
      const newWidth = resizeStartWidth.current + delta
      // Min 64px (2px padding + 18px button + 4px gap + 32px avatar + 8px padding), max 256px
      setLeftColumnWidth(Math.max(64, Math.min(256, newWidth)))
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing])

  useEffect(() => {
    if (!timelineContainerRef.current) return

    const updateWidth = () => {
      setTimelineContainerWidth(timelineContainerRef.current?.clientWidth || 0)
    }

    updateWidth()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth)
      return () => window.removeEventListener('resize', updateWidth)
    }

    const observer = new ResizeObserver(updateWidth)
    observer.observe(timelineContainerRef.current)
    return () => observer.disconnect()
  }, [leftColumnWidth])

  const hasNative = document && (document.elementsFromPoint || (document as any)?.msElementsFromPoint)

  function getDropTargetElementsAtPoint(x, y, dropTargets) {
    return dropTargets.filter((t) => {
      const rect = t.getBoundingClientRect()
      return x >= rect.left && x <= rect.right && y <= rect.bottom && y >= rect.top
    })
  }

  // TouchBackend options — only used on touch devices
  const _touchBackendOptions = {
    getDropTargetElementsAtPoint: !hasNative && getDropTargetElementsAtPoint,
    enableMouseEvents: true,
    delayTouchStart: 150
  }

  return (
    <div className={classes.root}>
      <DndProvider
        backend={isTouchCapable ? TouchBackend : HTML5Backend}
        options={isTouchCapable ? _touchBackendOptions : undefined}
      >
        <GlobalCalendarDragLayer zoomLevel={zoomLevel} cellWidth={timelineCellWidth} />
        <div className={classes.unscheduledContainer}>
          <UnscheduledJobs />
        </div>
        <div className={classes.scheduleBody}>
          {/* Timeline zoom */}
          <Hidden smUp>
            <div className={classes.mobileTimelineFilterHeader}>
              <div>
                <Typography variant="h3">Timeline</Typography>
              </div>
              <div className={classes.unscheduledFilterButtonContainer}>
                {hasTimelineActiveFilters && (
                  <>
                    <Hidden xsDown>
                      <Button variant="text" onClick={clearTimelineFilters} title="Clear all filters" icon={CloseIcon}>
                        <Typography variant="subtitle1">Clear</Typography>
                      </Button>
                    </Hidden>
                    <Hidden smUp>
                      <IconButton onClick={clearTimelineFilters} title="Clear all filters" size="small">
                        <CloseIcon className={classes.unscheduledCloseIcon} />
                      </IconButton>
                    </Hidden>
                  </>
                )}
                <>
                  <Hidden xsDown>
                    <div className={classes.mobileFilterButton}>
                      <Button
                        variant="contained"
                        icon={FilterListIcon}
                        onClick={(ev) => {
                          ev.stopPropagation()
                          setTimelineFilterOpen(!timelineFilterOpen)
                        }}
                        className={timelineDesktopFilterButtonClassName}
                      >
                        <Typography variant="body1">Filter</Typography>
                      </Button>
                      {hasTimelineActiveFilters && (
                        <span className={classes.mobileFilterCount}>{timelineActiveFilterCount}</span>
                      )}
                    </div>
                  </Hidden>
                  <Hidden smUp>
                    <div className={classes.mobileFilterButton}>
                      <Button
                        variant="contained"
                        onClick={(ev) => {
                          ev.stopPropagation()
                          setTimelineFilterOpen(!timelineFilterOpen)
                        }}
                        className={timelineMobileFilterButtonClassName}
                        title="Filter"
                      >
                        <FilterListIcon />
                      </Button>
                      {hasTimelineActiveFilters && (
                        <span className={classes.mobileFilterCount}>{timelineActiveFilterCount}</span>
                      )}
                    </div>
                  </Hidden>
                </>
                <IconButton onClick={handleZoomIn} disabled={!canZoomIn}>
                  <ZoomInIcon />
                </IconButton>
                <IconButton onClick={handleZoomOut} disabled={!canZoomOut}>
                  <ZoomOutIcon />
                </IconButton>
              </div>
            </div>
          </Hidden>
          <Hidden xsDown>
            <div className={classes.timelineFilterHeader}>
              <div>
                <Typography variant="h3">Timeline</Typography>
                <Typography variant="subtitle1">Viewing by {zoomLevel}</Typography>
              </div>
              <div className={classes.unscheduledFilterButtonContainer}>
                {hasTimelineActiveFilters && (
                  <>
                    <Hidden xsDown>
                      <Button variant="text" onClick={clearTimelineFilters} title="Clear all filters" icon={CloseIcon}>
                        <Typography variant="subtitle1">Clear</Typography>
                      </Button>
                    </Hidden>
                    <Hidden smUp>
                      <IconButton onClick={clearTimelineFilters} title="Clear all filters" size="small">
                        <CloseIcon className={classes.unscheduledCloseIcon} />
                      </IconButton>
                    </Hidden>
                  </>
                )}
                <div className={classes.mobileFilterButton}>
                  <Button
                    variant="contained"
                    icon={FilterListIcon}
                    size="small"
                    className={timelineDesktopFilterButtonClassName}
                    onClick={(ev) => {
                      ev.stopPropagation()
                      setTimelineFilterOpen(!timelineFilterOpen)
                    }}
                  >
                    <Typography variant="body1">Filter</Typography>
                  </Button>
                  {hasTimelineActiveFilters && (
                    <span className={classes.mobileFilterCount}>{timelineActiveFilterCount}</span>
                  )}
                </div>
                <IconButton onClick={handleZoomIn} disabled={!canZoomIn}>
                  <ZoomInIcon />
                </IconButton>
                <IconButton onClick={handleZoomOut} disabled={!canZoomOut}>
                  <ZoomOutIcon />
                </IconButton>
              </div>
            </div>
          </Hidden>

          {/* Timeline filters */}
          {timelineFilterOpen && (
            <div className={classes.timelineFilterBody}>
              <div className={classes.timelineFilterInputContainer}>
                <div className={classes.timelineFilterTopDiv}>
                  {/* Price Range */}
                  <Grid container alignItems="flex-end" xs={12} spacing={1}>
                    <Grid item xs={6} md={3} style={{ paddingRight: 4 }}>
                      <KeypadInputField
                        label={`Amount (${currency.symbol})`}
                        margin="none"
                        placeholder="Min"
                        format="dimension"
                        fullWidth
                        allowClear
                        value={minTimelinePrice}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          const { value } = event.target
                          setMinTimelinePrice(value === '' ? null : Number(value))
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} md={3} style={{ paddingLeft: 4 }}>
                      <KeypadInputField
                        margin="none"
                        flipX
                        placeholder="Max"
                        format="dimension"
                        value={maxTimelinePrice}
                        fullWidth
                        allowClear
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          const { value } = event.target
                          setMaxTimelinePrice(value === '' ? null : Number(value))
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DropdownSelect
                        label={`${quoteLabel.value} Type`}
                        variant="multi"
                        options={typeOptions}
                        hideCloseButton
                        placeholder={{
                          selectPlaceholder: `Select ${quoteLabel.value.toLowerCase()} type`,
                          plural: `Types`
                        }}
                        value={selectedProjectTypes.map((val) => {
                          return {
                            value: val,
                            label: getProjectTypeLabel(val, options)
                          }
                        })}
                        onChange={(selectedOption) => {
                          setSelectedProjectTypes(selectedOption.map((val) => val.value))
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Grid container alignItems="flex-end" item xs={12} spacing={1}>
                    <Grid item xs={12} md={6}>
                      <EstimatorSelect
                        name="estimator"
                        label="Estimator"
                        variant="multi"
                        value={selectedTimelineSalesReps?.map((val) => addAuth0Prefix(val as string))}
                        roles={['sales', 'admin']}
                        placeholder={{
                          selectPlaceholder: 'Any estimator',
                          plural: 'Estimators'
                        }}
                        onChange={(options: DropdownSelectOption[]) => {
                          setSelectedTimelineSalesReps(
                            options.map((option) => removeAuth0Prefix(option.value) as string)
                          )
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <EstimatorSelect
                        name="projectManager"
                        label={`Project Manager`}
                        variant="multi"
                        value={selectedProjectManagers?.map((val) => addAuth0Prefix(val as string))}
                        roles={['sales', 'admin']}
                        placeholder={{
                          selectPlaceholder: 'Select manager(s)',
                          plural: 'Project Managers'
                        }}
                        onChange={(options: DropdownSelectOption[]) => {
                          setSelectedProjectManagers(options.map((option) => removeAuth0Prefix(option.value) as string))
                        }}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </div>
              </div>
              {/* Action Buttons */}
              <div className={classes.timelineFilterInnerButtonContainer}>
                <Button variant="text" onClick={clearTimelineFilters}>
                  Clear
                </Button>
                <Button variant="contained" onClick={() => handleSearch()}>
                  Search
                </Button>
              </div>
            </div>
          )}

          <div className={classes.agendaContent}>
            {/* Navigation and Today button spanning full timeline width */}
            <div className={classes.navigationSpan}>
              <Typography weight={500} className={classes.dateLabel}>
                {getDateLabel()}
              </Typography>
              <div style={{ display: 'flex', gap: 4 }}>
                <Button
                  onClick={handleNavigatePrevious}
                  className={classes.navigationButton}
                  variant={'text'}
                  title="Previous"
                  size="small"
                  icon={ArrowBackIosIcon}
                  flipIconSide
                />
                <Button onClick={handleGoToToday} variant={'text'} size="small">
                  Today
                </Button>
                <Button
                  onClick={handleNavigateNext}
                  className={classes.navigationButton}
                  variant={'text'}
                  iconSize="small"
                  title="Next"
                  icon={ArrowForwardIosIcon}
                />
              </div>
            </div>
            {visibleProjectManagerIds.length === 0 ? (
              <div className={classes.noDataContainer}>
                {loading ? (
                  <Spinner />
                ) : (
                  <>
                    <Typography>
                      {hasTimelineActiveFilters ? (
                        'No scheduled jobs match your current filters. Try adjusting your filter criteria or clearing filters to see all jobs.'
                      ) : (
                        <>
                          Crews are needed to schedule jobs. Go to{' '}
                          <Link to={'/settings?section=billing-users&outerTab=crew'}>User Management</Link> to create
                          and manage crews.
                        </>
                      )}
                    </Typography>
                    {hasTimelineActiveFilters && (
                      <Button variant={'text'} onClick={clearTimelineFilters}>
                        Clear All Filters
                      </Button>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className={classes.agendaFlexContainer} ref={timelineContainerRef}>
                <div
                  ref={swipeIndicatorLeftRef}
                  className={[classes.swipeIndicator, classes.swipeIndicatorLeft].join(' ')}
                  style={{ display: 'none' }}
                >
                  <ArrowBackIosIcon fontSize="small" />
                </div>
                <div
                  ref={swipeIndicatorRightRef}
                  className={[classes.swipeIndicator, classes.swipeIndicatorRight].join(' ')}
                  style={{ display: 'none' }}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </div>
                {/* Wrapper for synchronized scrolling - both horizontal and vertical */}
                <div
                  className={classes.agendaFlexScroll}
                  ref={scrollContainerRef}
                  {...(isTouchCapable
                    ? {
                        onTouchStart: handleScrollTouchStart,
                        onTouchMove: handleScrollTouchMove,
                        onTouchEnd: handleScrollTouchEnd,
                        onTouchCancel: handleScrollTouchEnd
                      }
                    : {})}
                >
                  {/* Inner wrapper to hold both columns side by side */}
                  <div className={classes.agendaMinWidth}>
                    {/* Fixed Left Column - Project Managers */}
                    <div className={classes.agendaLeftColumn} style={{ width: `${leftColumnWidth}px` }}>
                      {/* Day headers spacer */}
                      <div className={classes.agendaDayHeaderSpacer}></div>
                      {/* Lead rows - just the left column parts */}
                      <div className={classes.agendaLeadRows}>
                        {visibleProjectManagerIds.map((projectManagerId, index) => {
                          const managersCrews = getCrewsForProjectManager(crews, projectManagerId)
                          const projectManagerEvents = filteredScheduledEvents.filter((event) =>
                            managersCrews.some((crew) => crew._id === event.crew)
                          )
                          const projectManager = users.find((user) => user.user_id === projectManagerId)
                          if (!projectManager) return null
                          // Group events by crew to calculate proper heights
                          const eventsByCrewId = projectManagerEvents.reduce(
                            (acc, event) => {
                              const crewId = event.crew || 'no-crew'
                              if (!acc[crewId]) acc[crewId] = []
                              acc[crewId].push(event)
                              return acc
                            },
                            {} as Record<string, CalendarEvent[]>
                          )
                          const crewIds = getCrewIdsForProjectManager({
                            projectManagerId,
                            crews,
                            projectManagerEvents,
                            crewOrder: crewOrderByLead[projectManagerId]
                          })

                          const totalRevenue = projectManagerEvents
                            .filter((p) => p.progress !== 100)
                            .reduce((sum, event) => {
                              return sum + (event.deal.value || 0)
                            }, 0)

                          const handleAvatarClick = () => {
                            if (leftColumnWidth <= 100) {
                              setShowNameTooltip(index)
                              // Auto-hide after 2 seconds
                              setTimeout(() => setShowNameTooltip(null), 2000)
                            }
                          }

                          return (
                            <>
                              <div key={projectManagerId} className={classes.agendaManagerRow}>
                                {/* Project Manager header - with drag and drop and collapse support */}
                                <AgendaHeaderLeftColumn
                                  index={index}
                                  projectManager={projectManager}
                                  totalRevenue={totalRevenue}
                                  isExpanded={!collapsedLeads[projectManagerId]}
                                  showControls={showProjectManagerControls}
                                  onMoveLead={handleMoveManager}
                                  onToggleExpand={() => {
                                    setCollapsedLeads((prev) => ({
                                      ...prev,
                                      [projectManagerId]: !prev[projectManagerId]
                                    }))
                                  }}
                                  handleAvatarClick={handleAvatarClick}
                                  leftColumnWidth={leftColumnWidth}
                                />
                              </div>
                              <div className={classes.agendaLeadManagerTooltipContainer}>
                                {/* Mobile Name Tooltip */}
                                {showNameTooltip === index && leftColumnWidth <= 100 && (
                                  // TODO: Fix tooltip
                                  <div className={classes.nameTooltip}>
                                    {projectManager.name}
                                    {/* <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#1e293b]"></div> */}
                                    <div className={classes.tooltipArrow}></div>
                                  </div>
                                )}
                              </div>

                              {!collapsedLeads[projectManagerId] &&
                                crewIds.map((crewId) => {
                                  const crew = crews.find((c) => c._id === crewId)
                                  const displayCrew = crew
                                    ? {
                                        id: crew._id,
                                        name: crew.name,
                                        color: crew.color,
                                        siteLeadName: crew.siteLead
                                          ? `${crew?.siteLead?.firstName} ${crew?.siteLead?.lastName}`
                                          : ''
                                      }
                                    : {
                                        id: crewId,
                                        name: 'Unassigned',
                                        color: '#94a3b8',
                                        siteLeadName: undefined
                                      }
                                  const crewEvents = eventsByCrewId[crewId] || []
                                  const minHeight = calculateCrewHeight(crewEvents)
                                  // Get crew initials (two letters)
                                  const getCrewInitials = () => {
                                    if (!displayCrew.name) return '?'
                                    const words = displayCrew.name.split(' ')
                                    if (words.length >= 2) {
                                      // Take first letter of first two words
                                      return (words[0][0] + words[1][0]).toUpperCase()
                                    }
                                    // If single word, take first two letters
                                    return displayCrew.name.substring(0, 2).toUpperCase()
                                  }
                                  return (
                                    <CrewLeftColumnItem
                                      key={crewId}
                                      displayCrew={displayCrew}
                                      minHeight={minHeight}
                                      leftColumnWidth={leftColumnWidth}
                                      crewInitials={getCrewInitials()}
                                      projectManager={projectManager}
                                      onMoveLead={handleMoveManager}
                                    />
                                  )
                                })}
                            </>
                          )
                        })}
                      </div>
                      <div
                        className={classes.agendaResizeHandleButton}
                        style={{ paddingLeft: leftColumnWidth === 200 ? '4px' : '3px' }}
                        onClick={() => setLeftColumnWidth((current) => (current === 72 ? 200 : 72))}
                      >
                        <ArrowBackIosIcon
                          style={{
                            height: '30px',
                            width: '15px',
                            transform: leftColumnWidth === 72 ? 'rotate(180deg)' : undefined,
                            transition: 'transform 0.3s',
                            marginLeft: leftColumnWidth === 72 ? '-5px' : undefined
                          }}
                        />
                      </div>
                    </div>
                    <div className={classes.agendaLeftColumnInner}>
                      <div className={classes.agendaTimelineHeader}>
                        <TimelineHeader
                          startDate={startDate}
                          endDate={endDate}
                          zoomLevel={zoomLevel}
                          cellWidth={timelineCellWidth}
                          hoveredDate={hoveredDate}
                          onDateHover={setHoveredDate}
                        />
                        <HoverEventShadow hoveredEventShadow={hoveredEventShadow} classes={classes} />
                        <DragHoverShadow
                          startDate={startDate}
                          endDate={endDate}
                          cellWidth={timelineCellWidth}
                          crews={crews}
                          classes={classes}
                        />
                        <DragAutoScroll />
                      </div>
                      <div className={classes.agendaLeadRows}>
                        {visibleProjectManagerIds.map((projectManagerId) => {
                          const projectManagersCrews = getCrewsForProjectManager(crews, projectManagerId)
                          const projectManagerEvents = filteredScheduledEvents.filter((event) =>
                            projectManagersCrews.some((crew) => crew._id === event.crew)
                          )
                          const crewIds = getCrewIdsForProjectManager({
                            projectManagerId,
                            crews,
                            projectManagerEvents,
                            crewOrder: crewOrderByLead[projectManagerId]
                          })

                          return (
                            <ProjectManagerRow
                              key={projectManagerId}
                              projectManagerId={projectManagerId}
                              events={projectManagerEvents}
                              crewIds={crewIds}
                              startDate={startDate}
                              endDate={endDate}
                              onProjectHover={setHoveredProject}
                              hoveredProject={hoveredProject}
                              hoveredDate={hoveredDate}
                              onDateHover={setHoveredDate}
                              onDrop={handleDrop}
                              zoomLevel={zoomLevel}
                              cellWidth={timelineCellWidth}
                              onEventUpdate={updateEvent}
                              // onMoveLead={handleMoveLead}
                              // onMoveCrewWithinLead={handleMoveCrewWithinLead}
                              crewOrder={crewOrderByLead[projectManagerId]}
                              isExpanded={!collapsedLeads[projectManagerId]}
                            />
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Resizable handle - positioned over the border */}
                {/* <div
                  className={classes.agendaResizeHandle}
                  style={{ left: `${leftColumnWidth}px` }}
                  onMouseDown={handleResizeStart}
                /> */}
              </div>
            )}
          </div>
        </div>
      </DndProvider>
    </div>
  )
}

export default EventsAgendaView
